<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useFetch } from '@/composables/useFetch';
import AncestorsChain from './AncestorsChain.vue';
import ProjectRelationships from './ProjectRelationships.vue';
import PublicSection from './PublicSection.vue';
import BulkMakePublicSection from './BulkMakePublicSection.vue';
import QuickPublishSection from './QuickPublishSection.vue';
import type { PagePropertiesData } from './types';

const $fetch = useFetch();

const settings = (window as any).drupalSettings?.epbPageProperties || {};
const nodeId: number = settings.nodeId || 0;

const data = ref<PagePropertiesData | null>(null);
// Authoritative manage flag comes from the payload (server-gated); the
// drupalSettings value is only an initial hint.
const canManage = computed(() => data.value?.can_manage ?? false);
const loading = ref(true);
const error = ref<string | null>(null);

const base = computed(() => `/epbapi/page-properties/${nodeId}`);

async function load() {
  loading.value = true;
  error.value = null;
  try {
    data.value = await $fetch(base.value);
  } catch (e: any) {
    error.value = e?.message || 'Failed to load page properties.';
  } finally {
    loading.value = false;
  }
}

async function togglePublic(makePublic: boolean) {
  const res = await $fetch(`${base.value}/public`, {
    method: 'POST',
    body: { make_public: makePublic },
  });
  if (data.value && res?.success) {
    data.value.public.is_public = res.is_public;
    await load();
  }
}

async function saveRelationships(groupIds: number[]) {
  const res = await $fetch(`${base.value}/relationships`, {
    method: 'POST',
    body: { group_ids: groupIds },
  });
  if (res?.success) {
    await load();
  }
}

async function quickPublish(publish: boolean) {
  const res = await $fetch(`${base.value}/quick-publish`, {
    method: 'POST',
    body: { publish, scope: 'collection' },
  });
  if (data.value?.quick_publish && res?.applicable) {
    data.value.quick_publish = res;
  }
  // Bulk publication changes what the other islands on this page render
  // (areas, pathways, lists…). Those are independent Vue apps with no shared
  // store, so a reload is the reliable way to reflect the new state for the
  // whole — and unknown — set of blocks. Only when something actually changed.
  if (res?.affected > 0) {
    window.location.reload();
  }
}

async function bulkMakePublic(makePublic: boolean) {
  const res = await $fetch(`${base.value}/bulk-public`, {
    method: 'POST',
    body: { make_public: makePublic, scope: 'collection' },
  });
  if (data.value?.bulk_public && res?.applicable) {
    data.value.bulk_public = res;
  }
  // Making content public/private changes what the other islands (and anonymous
  // visitors) render; reload to reflect it across the whole page when something
  // actually changed.
  if (res?.affected > 0) {
    window.location.reload();
  }
}

onMounted(load);
</script>

<template>
  <div class="epb-page-properties">
    <div v-if="loading" class="text-muted small py-2">
      <span class="spinner-border spinner-border-sm me-2" role="status"></span>
      Loading page properties…
    </div>

    <div v-else-if="error" class="alert alert-warning py-2 small">
      {{ error }}
    </div>

    <template v-else-if="data">
      <AncestorsChain :ancestors="data.ancestors" />

      <template v-if="canManage">
        <ProjectRelationships :relationships="data.relationships" />

        <PublicSection
          :is-public="data.public.is_public"
          :relationships="data.relationships"
          :available-groups="data.available_groups"
          :can-manage="canManage"
          @toggle-public="togglePublic"
          @save-relationships="saveRelationships"
        />

        <BulkMakePublicSection
          v-if="data.is_collection && data.bulk_public.applicable"
          :status="data.bulk_public"
          :can-manage="canManage"
          @bulk-make-public="bulkMakePublic"
        />

        <QuickPublishSection
          v-if="data.is_collection && data.quick_publish.applicable"
          :status="data.quick_publish"
          :can-manage="canManage"
          @quick-publish="quickPublish"
        />
      </template>
    </template>
  </div>
</template>

<style scoped>
.epb-page-properties {
  font-size: 0.95rem;
}
.epb-page-properties :deep(.pp-section) {
  margin-bottom: 1.25rem;
}
.epb-page-properties :deep(.pp-section-title) {
  text-decoration: underline;
  font-weight: 600;
  margin-bottom: 0.5rem;
}
</style>
